import { useCallback, useSyncExternalStore, useMemo, useEffect, useState } from 'react';
import { habitData, Habit } from '../data/habitData';
import { HabitEntity } from '../types/data';
import { habitRepo } from '../utils/repos/habitRepo';
import { ensureDefaultHabit, isShoppingHabitId } from '../utils/habits';
import { TODO_SYNC_STATE_KEYS, todoLocalRepo } from '../utils/localDb/todoLocalRepo';

const STORAGE_KEY = 'habits';
const BACKEND = (process.env.REACT_APP_BACKEND || 'local').toLowerCase();

// 変換: HabitEntity (snake_case) -> Habit (camelCase)
const habitToCamelCase = (e: HabitEntity): Habit => ({
  id: e.id,
  title: e.title,
  body: e.body,
  createdAt: e.created_at,
  modifiedAt: e.modified_at,
  deleted: e.is_deleted,
  lastDoneOn: e.last_done_on,
  doneCycleDate: e.done_cycle_date,
  sortOrder: e.sort_order,
});

// 変換: Habit (camelCase) -> HabitEntity (snake_case)
const habitToSnakeCase = (h: Habit): HabitEntity => ({
  id: h.id,
  title: h.title,
  body: h.body,
  created_at: h.createdAt,
  modified_at: h.modifiedAt,
  is_deleted: h.deleted,
  last_done_on: h.lastDoneOn,
  done_cycle_date: h.doneCycleDate,
  sort_order: h.sortOrder,
});

const parseStoredHabits = (storedData: string): Habit[] => {
  try {
    const parsed = JSON.parse(storedData);
    if (!Array.isArray(parsed)) return [];
    return parsed.map((item: any) => ('created_at' in item ? habitToCamelCase(item as HabitEntity) : (item as Habit)));
  } catch {
    return [];
  }
};

// External store
type HabitsListener = () => void;
let habitsState: Habit[] = (() => {
  if (BACKEND === 'supabase') return [];
  const stored = localStorage.getItem(STORAGE_KEY);
  const fromStorage = stored ? parseStoredHabits(stored) : habitData.map(habitToCamelCase);
  // Ensure shopping in local mode
  const hasShopping = fromStorage.some(h => isShoppingHabitId(h.id));
  if (!hasShopping) {
    const shopping = habitToCamelCase(habitData.find(h => h.id === 'habit-shopping')!);
    return [shopping, ...fromStorage];
  }
  return fromStorage;
})();

const subscribers = new Set<HabitsListener>();

const emit = () => {
  subscribers.forEach(fn => {
    try { fn(); } catch {}
  });
};

const saveHabitsToStorage = (habits: Habit[]) => {
  try {
    const entities = habits.map(habitToSnakeCase);
    localStorage.setItem(STORAGE_KEY, JSON.stringify(entities));
  } catch (e) {
    console.error('Failed to save habits to localStorage:', e);
  }
};

const getSnapshot = () => habitsState;
const subscribe = (listener: HabitsListener) => {
  subscribers.add(listener);
  return () => subscribers.delete(listener);
};

const setHabitsStore = (next: Habit[] | ((prev: Habit[]) => Habit[])) => {
  habitsState = typeof next === 'function' ? (next as (p: Habit[]) => Habit[])(habitsState) : next;
  saveHabitsToStorage(habitsState);
  emit();
};

const cacheHabits = (habits: Habit[]): void => {
  if (BACKEND !== 'supabase') return;
  todoLocalRepo.upsertHabits(habits.map(habitToSnakeCase))
    .catch((e) => console.error('Failed to cache habits in IndexedDB:', e));
};

const cacheHabitEntities = (habits: HabitEntity[]): void => {
  if (BACKEND !== 'supabase') return;
  todoLocalRepo.upsertHabits(habits)
    .catch((e) => console.error('Failed to cache habits in IndexedDB:', e));
};

// Sync changes from other tabs
if (typeof window !== 'undefined') {
  window.addEventListener('storage', (e) => {
    if (e.key === STORAGE_KEY) {
      const stored = localStorage.getItem(STORAGE_KEY);
      habitsState = stored ? parseStoredHabits(stored) : habitData.map(habitToCamelCase);
      emit();
    }
  });
}

export const useHabits = () => {
  const habits = useSyncExternalStore(subscribe, getSnapshot, () => []);
  const [syncRequest, setSyncRequest] = useState(0);
  const refreshFromServer = useCallback(() => {
    if (BACKEND === 'supabase') setSyncRequest(previous => previous + 1);
  }, []);

  // Load from Supabase and ensure shopping habit exists
  useEffect(() => {
    if (BACKEND !== 'supabase') return;
    let alive = true;
    (async () => {
      try {
        try {
          const cachedRows = await todoLocalRepo.listHabits();
          if (alive && cachedRows.length > 0) {
            habitsState = cachedRows.map(habitToCamelCase);
            emit();
          }
        } catch (cacheReadError) {
          console.error('Failed to load habits from IndexedDB:', cacheReadError);
        }

        await ensureDefaultHabit('shopping');
        const habitCursor = await todoLocalRepo.getSyncCursor(TODO_SYNC_STATE_KEYS.habitsLastSyncedAt);
        const rows = habitCursor
          ? await habitRepo.listHabitsModifiedAfter(habitCursor)
          : await habitRepo.listHabits();
        try {
          await todoLocalRepo.upsertHabits(rows);
          await todoLocalRepo.setHabitsSyncCursorFromRows(rows);
        } catch (cacheError) {
          console.error('Failed to cache habits in IndexedDB:', cacheError);
        }
        if (!alive) return;
        const latestRows = await todoLocalRepo.listHabits();
        if (!alive) return;
        const list = latestRows.map(habitToCamelCase);
        habitsState = list;
        emit();
      } catch (e) {
        console.error('Failed to load habits from Supabase:', e);
      }
    })();
    return () => { alive = false; };
  }, [syncRequest]);

  const activeHabits = useMemo(() => {
    return habits
      .filter(h => !h.deleted)
      .slice()
      .sort((a, b) => {
        const ao = a.sortOrder ?? 0;
        const bo = b.sortOrder ?? 0;
        if (ao !== bo) return ao - bo;
        return new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime();
      });
  }, [habits]);

  const createHabit = useCallback(async (input: Omit<Habit, 'id' | 'createdAt' | 'modifiedAt' | 'deleted'>): Promise<Habit> => {
    if (input.title.trim() === '買い物') {
      console.warn('「買い物」は予約語のため、新規作成できません');
      throw new Error('「買い物」は予約語のため、新規作成できません');
    }
    const now = new Date().toISOString();
    const nextSortOrder = Math.max(
      0,
      ...activeHabits.filter(h => !isShoppingHabitId(h.id)).map(h => h.sortOrder ?? 0)
    ) + 1000;
    if (BACKEND === 'supabase') {
      const saved = await habitRepo.createHabit({ title: input.title, body: input.body, sort_order: nextSortOrder });
      cacheHabitEntities([saved]);
      const h: Habit = {
        id: saved.id,
        title: saved.title,
        body: saved.body,
        createdAt: saved.created_at,
        modifiedAt: saved.modified_at,
        deleted: saved.is_deleted,
        lastDoneOn: saved.last_done_on,
        doneCycleDate: saved.done_cycle_date,
        sortOrder: saved.sort_order,
      };
      setHabitsStore(prev => [...prev, h]);
      return h;
    }
    const newHabit: Habit = {
      ...input,
      id: `habit-${Date.now()}-${Math.random().toString(36).substring(2, 9)}`,
      deleted: false,
      createdAt: now,
      modifiedAt: now,
      sortOrder: nextSortOrder,
    };
    setHabitsStore(prev => [...prev, newHabit]);
    return newHabit;
  }, [activeHabits]);

  const reorderHabits = useCallback(async (sourceId: string, targetId: string, insertAfter: boolean) => {
    if (isShoppingHabitId(sourceId) || isShoppingHabitId(targetId)) return;

    const ordered = [...habits]
      .filter(h => !h.deleted && !isShoppingHabitId(h.id))
      .sort((a, b) => {
        const ao = a.sortOrder ?? 0;
        const bo = b.sortOrder ?? 0;
        if (ao !== bo) return ao - bo;
        return new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime();
      });
    const sourceIndex = ordered.findIndex(h => h.id === sourceId);
    const targetIndex = ordered.findIndex(h => h.id === targetId);
    if (sourceIndex < 0 || targetIndex < 0) return;
    if (sourceIndex === targetIndex) return;

    const next = [...ordered];
    const [moved] = next.splice(sourceIndex, 1);
    const insertionIndex = (() => {
      const adjustedTargetIndex = sourceIndex < targetIndex ? targetIndex - 1 : targetIndex;
      return insertAfter ? adjustedTargetIndex + 1 : adjustedTargetIndex;
    })();
    next.splice(insertionIndex, 0, moved);

    const prevNeighbor = insertionIndex > 0 ? next[insertionIndex - 1] : undefined;
    const nextNeighbor = insertionIndex < next.length - 1 ? next[insertionIndex + 1] : undefined;
    const prevOrder = prevNeighbor?.sortOrder ?? 0;
    const nextOrder = nextNeighbor?.sortOrder;
    const updatedAt = new Date().toISOString();

    const shouldRenumber = (() => {
      if (nextNeighbor && nextOrder !== undefined && nextOrder - prevOrder <= 1) return true;
      if (!prevNeighbor && nextNeighbor && nextOrder !== undefined && nextOrder <= 1000) return true;
      return false;
    })();

    if (shouldRenumber) {
      const renumbered = next.map((h, i) => ({
        ...h,
        sortOrder: (i + 2) * 1000, // keep 1000 for shopping
        modifiedAt: updatedAt,
      }));
      setHabitsStore(prev => prev.map(h => {
        const r = renumbered.find(x => x.id === h.id);
        return r ? { ...h, sortOrder: r.sortOrder, modifiedAt: r.modifiedAt } : h;
      }));
      cacheHabits(renumbered);
      if (BACKEND === 'supabase') {
        await Promise.all(renumbered.map(h => habitRepo.updateHabit(h.id, { sort_order: h.sortOrder } as any)));
      }
      return;
    }

    const computedSortOrder = (() => {
      if (!prevNeighbor) {
        if (!nextNeighbor || nextOrder === undefined) return 2000;
        return nextOrder - 1000;
      }
      if (!nextNeighbor || nextOrder === undefined) return prevOrder + 1000;
      return Math.floor(prevOrder + (nextOrder - prevOrder) / 2);
    })();

    setHabitsStore(prev => prev.map(h => h.id === moved.id
      ? { ...h, sortOrder: computedSortOrder, modifiedAt: updatedAt }
      : h
    ));
    cacheHabits([{ ...moved, sortOrder: computedSortOrder, modifiedAt: updatedAt }]);
    if (BACKEND === 'supabase') {
      await habitRepo.updateHabit(moved.id, { sort_order: computedSortOrder } as any);
    }
  }, [habits]);

  const updateHabit = useCallback((id: string, updates: Partial<Omit<Habit, 'id' | 'createdAt'>>): boolean => {
    const updatedAt = new Date().toISOString();
    const existingHabit = habitsState.find(h => h.id === id);
    const updatedHabit = existingHabit ? {
      ...existingHabit,
      ...updates,
      modifiedAt: updatedAt,
    } : null;

    setHabitsStore(prev => {
      const index = prev.findIndex(h => h.id === id);
      if (index === -1) return prev;
      const next = [...prev];
      next[index] = { ...next[index], ...updates, modifiedAt: updatedAt };
      return next;
    });
    if (updatedHabit) {
      cacheHabits([updatedHabit]);
    }
    if (BACKEND === 'supabase') {
      const patch: Partial<HabitEntity> = {
        title: updates.title,
        body: updates.body,
        modified_at: updatedAt,
        is_deleted: updates.deleted,
        last_done_on: updates.lastDoneOn,
        done_cycle_date: updates.doneCycleDate,
        sort_order: updates.sortOrder,
      };
      habitRepo.updateHabit(id, patch).catch((e) => console.error('Failed to update habit on Supabase:', e));
    }
    return true;
  }, []);

  const deleteHabit = useCallback((id: string): boolean => {
    if (isShoppingHabitId(id)) {
      console.warn('「買い物」習慣は削除できません');
      return false;
    }
    if (BACKEND === 'supabase') {
      habitRepo.softDeleteHabit(id).catch((e) => console.error('Failed to delete habit on Supabase:', e));
    }
    return updateHabit(id, { deleted: true });
  }, [updateHabit]);

  const getHabitById = useCallback((id: string): Habit | undefined => {
    return habits.find(h => h.id === id);
  }, [habits]);

  const getActiveHabits = useCallback((): Habit[] => activeHabits, [activeHabits]);

  const resetToDefaultData = useCallback(() => {
    const next = habitData.map(habitToCamelCase);
    setHabitsStore(next);
  }, []);

  const clearStorage = useCallback(() => {
    try {
      localStorage.removeItem(STORAGE_KEY);
      const next = habitData.map(habitToCamelCase);
      setHabitsStore(next);
    } catch (e) {
      console.error('Failed to clear localStorage:', e);
    }
  }, []);

  return {
    habits,
    activeHabits,
    refreshFromServer,
    createHabit,
    reorderHabits,
    updateHabit,
    deleteHabit,
    getHabitById,
    getActiveHabits,
    resetToDefaultData,
    clearStorage,
  };
};

export default useHabits;
